import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const inp = "bg-muted/60 border h-9 px-2 text-sm";
const btn = "border h-8 w-8 flex items-center justify-center hover:border-primary";

const Row = ({ d, onDone }) => {
  const [edit, setEdit] = useState(null);
  const slug = d.name.replace(/\W+/g, "-").toLowerCase();
  const patch = (body) => api.patch("/departments", { name: d.name, ...body }).then((r) => { toast.success(r.data.message); setEdit(null); onDone(); }).catch((e) => toast.error(errMsg(e)));
  const remove = () => window.confirm(`Remove the ${d.name} department?`) && api.delete("/departments", { params: { name: d.name } })
    .then((r) => { toast.success(r.data.message); onDone(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <tr data-testid={`dept-row-${slug}`}>
      <td>{edit === null ? <span className="font-medium" data-testid={`dept-name-${slug}`}>{d.name}</span>
        : <input autoFocus data-testid={`dept-rename-input-${slug}`} className={`${inp} w-full`} value={edit} onChange={(e) => setEdit(e.target.value)} onKeyDown={(e) => e.key === "Enter" && patch({ new_name: edit })} />}</td>
      <td className="num text-xs" data-testid={`dept-count-${slug}`}>{d.employees ?? "—"}</td>
      <td><label className="flex items-center gap-1.5 text-xs" title="Sees Weekly Stars"><input type="checkbox" data-testid={`dept-stars-${slug}`} checked={d.stars} onChange={(e) => patch({ stars: e.target.checked })} /><span className="hidden sm:inline">Sees Weekly Stars</span></label></td>
      <td><div className="flex gap-1 justify-end">{edit === null ? <>
        <button data-testid={`dept-rename-${slug}`} title="Rename" onClick={() => setEdit(d.name)} className={btn}><Pencil className="h-3.5 w-3.5" /></button>
        <button data-testid={`dept-remove-${slug}`} title="Remove" onClick={remove} className={`${btn} hover:border-rose-400`}><Trash2 className="h-3.5 w-3.5" /></button></>
        : <><button data-testid={`dept-rename-save-${slug}`} title="Save" onClick={() => patch({ new_name: edit })} className={btn}><Check className="h-3.5 w-3.5" /></button>
          <button data-testid={`dept-rename-cancel-${slug}`} title="Cancel" onClick={() => setEdit(null)} className={btn}><X className="h-3.5 w-3.5" /></button></>}</div></td>
    </tr>
  );
};

export const DepartmentsDialog = ({ onClose }) => {
  const [d, setD] = useState(null);
  const [name, setName] = useState("");
  const load = () => api.get("/departments").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const add = () => api.post("/departments", { name }).then((r) => { toast.success(r.data.message); setName(""); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-card max-h-[90vh] overflow-y-auto" data-testid="departments-dialog">
        <DialogHeader><DialogTitle className="font-display">Departments</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Founders and HR can add, rename or remove departments. Renaming updates every employee in it. A department can't be removed while employees are in it.
          Tick "Sees Weekly Stars" to show the Weekly Follow-up Stars card to that department's staff on their first page (Sales always see it on Home).</p>
        <div className="flex gap-2">
          <input data-testid="dept-new-name" placeholder="New department name" className={`${inp} flex-1`} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && add()} />
          <button data-testid="dept-add" disabled={!name.trim()} onClick={add} className="bg-primary text-primary-foreground h-9 px-4 text-sm flex items-center gap-2 disabled:opacity-50"><Plus className="h-4 w-4" /> Add</button>
        </div>
        <div className="ax-scroll"><table className="ax-table w-full" data-testid="departments-table">
          <thead><tr><th>Department</th><th>Staff</th><th>Stars</th><th /></tr></thead>
          <tbody>{(d?.items || []).map((x) => <Row key={x.name} d={x} onDone={load} />)}</tbody>
        </table></div>
      </DialogContent>
    </Dialog>
  );
};
